import { Link } from './SmartLink';
import type { ElementType, ReactNode } from 'react';
import { CheckCircle2, ChevronDown, MessageCircle, Plane } from 'lucide-react';


type IconType = ElementType;

export interface FAQItem {
  q: string;
  a: string;
}

export interface TrustItem {
  Icon?: IconType;
  title: string;
  text?: string;
}

export interface ProcessItem {
  Icon: IconType;
  step: string;
  title: string;
  text: string;
}

export function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'center',
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  align?: 'center' | 'left';
}) {
  return (
    <div className={`bbt-section-header${align === 'left' ? ' bbt-section-header-left' : ''}`}>
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      {subtitle && <span>{subtitle}</span>}
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  trustItems,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  trustItems?: TrustItem[];
}) {
  return (
    <section className="bbt-hero">
      <div className="bbt-hero-cloud bbt-hero-cloud-left" aria-hidden="true" />
      <div className="bbt-hero-cloud bbt-hero-cloud-right" aria-hidden="true" />
      <svg className="bbt-route bbt-hero-route" viewBox="0 0 360 220" aria-hidden="true">
        <path d="M18 158 C86 42 166 194 228 92 C260 40 312 38 342 66" />
      </svg>
      <Plane className="bbt-hero-plane" size={42} aria-hidden="true" />
      <div className="bbt-hero-shell">
        <div className="bbt-hero-copy">
          <p>{eyebrow}</p>
          <h1>{title}</h1>
          <span>{subtitle}</span>
          {(primaryLabel || secondaryLabel) && (
            <div className="bbt-hero-actions">
              {primaryLabel && primaryHref && (
                <a className="bbt-whatsapp-button" href={primaryHref} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={20} /> {primaryLabel}
                </a>
              )}
              {secondaryLabel && secondaryHref && (
                secondaryHref.startsWith('/') ? (
                  <Link className="bbt-secondary-button" to={secondaryHref}>{secondaryLabel}</Link>
                ) : (
                  <a className="bbt-secondary-button" href={secondaryHref}>{secondaryLabel}</a>
                )
              )}
            </div>
          )}
        </div>
        {trustItems && trustItems.length > 0 && <TrustStrip items={trustItems} dark />}
      </div>
    </section>
  );
}

export function ServiceCard({ Icon, title, text }: { Icon: IconType; title: string; text: string }) {
  return (
    <article className="bbt-service-card">
      <span><Icon size={30} /></span>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </article>
  );
}

export function TrustStrip({ items, dark = false }: { items: TrustItem[]; dark?: boolean }) {
  return (
    <div className={`bbt-trust-strip${dark ? ' bbt-trust-strip-dark' : ''}`}>
      {items.map(({ Icon, title, text }) => (
        <div key={`${title}-${text || ''}`} className="bbt-trust-item">
          {Icon && <span><Icon size={22} /></span>}
          <div>
            <strong>{title}</strong>
            {text && <small>{text}</small>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProcessSteps({ items }: { items: ProcessItem[] }) {
  return (
    <div className="bbt-process-grid">
      <svg className="bbt-route bbt-process-route" viewBox="0 0 1100 160" aria-hidden="true">
        <path d="M70 96 C220 22 330 136 470 82 C630 24 738 130 888 72 C956 46 1018 42 1070 58" />
      </svg>
      <Plane className="bbt-process-plane" size={32} aria-hidden="true" />
      {items.map(({ Icon, step, title, text }, index) => (
        <article key={step} className={`bbt-process-card${index === items.length - 1 ? ' bbt-process-card-final' : ''}`}>
          <span><Icon size={30} /></span>
          <small>{step}</small>
          <h3>{title}</h3>
          <p>{text}</p>
        </article>
      ))}
    </div>
  );
}

export function FAQSection({
  eyebrow = 'FAQ',
  title,
  subtitle,
  faqs,
  help,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  faqs: FAQItem[];
  help?: ReactNode;
}) {
  return (
    <section className="bbt-faq-section">
      <div className="bbt-shell">
        <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />
        <div className="bbt-faq-layout">
          <div className="bbt-faq-list">
            {faqs.map((faq, index) => (
              <details key={faq.q} className="bbt-faq-item" open={index === 0}>
                <summary>
                  <span>{faq.q}</span>
                  <ChevronDown size={20} />
                </summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
          {help}
        </div>
      </div>
    </section>
  );
}

export function CTASection({
  title,
  subtitle,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  trust,
}: {
  title: string;
  subtitle: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  trust?: string[];
}) {
  return (
    <section className="bbt-final-cta">
      <div className="bbt-final-shell">
        <CheckCircle2 size={86} aria-hidden="true" />
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
          {trust && (
            <div className="bbt-final-trust">
              {trust.map((item) => <span key={item}><CheckCircle2 size={15} /> {item}</span>)}
            </div>
          )}
        </div>
        <div className="bbt-final-actions">
          <a className="bbt-final-whatsapp" href={primaryHref} target="_blank" rel="noopener noreferrer">
            <MessageCircle size={19} /> {primaryLabel}
          </a>
          {secondaryLabel && secondaryHref && (
            secondaryHref.startsWith('/') ? (
              <Link className="bbt-final-secondary" to={secondaryHref}>{secondaryLabel}</Link>
            ) : (
              <a className="bbt-final-secondary" href={secondaryHref}>{secondaryLabel}</a>
            )
          )}
        </div>
      </div>
    </section>
  );
}

